import {
    Box, ThemeProvider, Typography,
    List, ListItemButton, ListItemIcon, ListItemText,
    Divider, Chip, alpha, Paper, Tooltip, CircularProgress,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import React, { Suspense, lazy, useCallback, useMemo, useRef, useState } from 'react';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ForumIcon from '@mui/icons-material/Forum';
import MonitorHeartIcon      from '@mui/icons-material/MonitorHeart';
import SmartToyIcon          from '@mui/icons-material/SmartToy';
import ChatIcon              from '@mui/icons-material/Chat';
import BarChartIcon          from '@mui/icons-material/BarChart';
import AccountTreeIcon       from '@mui/icons-material/AccountTree';
import PsychologyIcon        from '@mui/icons-material/Psychology';
import FolderIcon            from '@mui/icons-material/Folder';
import CloudUploadIcon       from '@mui/icons-material/CloudUpload';
import ScienceIcon           from '@mui/icons-material/Science';
import DashboardIcon         from '@mui/icons-material/Dashboard';
import BugReportIcon         from '@mui/icons-material/BugReport';
import RateReviewIcon        from '@mui/icons-material/RateReview';
import ReceiptLongIcon       from '@mui/icons-material/ReceiptLong';
import OpenInNewIcon         from '@mui/icons-material/OpenInNew';
import TrackChangesIcon      from '@mui/icons-material/TrackChanges';
import MenuBookIcon          from '@mui/icons-material/MenuBook';
import LibraryBooksIcon      from '@mui/icons-material/LibraryBooks';
import BusinessIcon          from '@mui/icons-material/Business';
import TuneIcon              from '@mui/icons-material/Tune';
import PlaceIcon             from '@mui/icons-material/Place';
import PersonAddIcon         from '@mui/icons-material/PersonAdd';
import HistoryIcon           from '@mui/icons-material/History';
import ShieldIcon            from '@mui/icons-material/Shield';
import FlagIcon              from '@mui/icons-material/Flag';

import { synterraTheme } from './theme/theme';
import { T }            from './theme/tokens';
import AccessGate       from './components/AccessGate';
import AdminAccessControl from './components/AdminAccessControl';
import StaffAccessControl from './components/StaffAccessControl';
import { viewerRoleLabel, AuthIdentity } from './auth/identity';
import { hasAdminCode } from './config';
import { SynButton } from './components/ds';

import { PlatformIssueProvider } from './data/PlatformIssueStore';
import { InitiativeProvider, useInitiativeContext } from './context/InitiativeContext';
import BugReportButton from './components/shared/BugReportButton';
import { usePitchMode } from './hooks/usePitchMode';
import { useMeasureHubEnabled } from './hooks/useMeasureHubEnabled';
import StatusBanner from './components/StatusBanner';
import PanelChunkErrorBoundary from './components/shared/PanelChunkErrorBoundary';
import SafetyDisclaimer from './components/SafetyDisclaimer';
import { useActiveInitiative } from './hooks/useActiveInitiative';

// ── Lazy-loaded panels (bundle-splitting) ────────────────────────────────────
// Every screen-level panel is code-split with React.lazy so the initial bundle
// only carries the app shell (sidebar, top bar, access gate, providers). Each
// panel's chunk is fetched on first activation; <Suspense> below shows a
// neutral spinner while it loads. This keeps behavior identical (same PANELS
// indices, deep links, pitch mode) while cutting the main chunk size.
const AIChat              = lazy(() => import('./components/AIChat'));
const TAProviderWorkspace = lazy(() => import('./components/TAProviderWorkspace'));
const BulkUpload          = lazy(() => import('./components/BulkUpload'));
const CARIIntelligence    = lazy(() => import('./components/CARIIntelligence'));
const CFIRDashboard       = lazy(() => import('./components/CFIRDashboard'));
const FiveDsDashboard     = lazy(() => import('./components/FiveDsDashboard'));
const HealthView          = lazy(() => import('./components/HealthView'));
const Verticals           = lazy(() => import('./components/Verticals'));
const VerticalRegistry    = lazy(() => import('./components/VerticalRegistry'));
const IntegrationHealthDashboard = lazy(() => import('./components/IntegrationHealthDashboard'));
const AIUsageGovernance   = lazy(() => import('./components/AIUsageGovernance'));
const SimulationDashboard = lazy(() => import('./components/SimulationDashboard'));
const HELIOSDashboard     = lazy(() => import('./components/HELIOSDashboard'));
const InternalDashboard      = lazy(() => import('./components/internal/InternalDashboard'));
const PlatformIssueDashboard = lazy(() => import('./components/internal/PlatformIssueDashboard'));
const TAReviewQueue          = lazy(() => import('./components/internal/TAReviewQueue'));
const RecordReviewDashboard  = lazy(() => import('./components/internal/RecordReviewDashboard'));
const CostCaptureDashboard   = lazy(() => import('./components/internal/CostCaptureDashboard'));
const CostLedger             = lazy(() => import('./components/internal/CostLedger'));
const ScenarioLibrary        = lazy(() => import('./components/internal/ScenarioLibrary'));
const TestingFeedbackDashboard = lazy(() => import('./components/internal/TestingFeedbackDashboard'));
const PeerExchange           = lazy(() => import('./components/PeerExchange'));
const SourceCapturePanel     = lazy(() => import('./components/internal/SourceCapturePanel'));
const CedarLeafArea          = lazy(() => import('./components/internal/CedarLeafArea'));
const SuggestSource          = lazy(() => import('./components/internal/SuggestSource'));
const LeafReviewQueue        = lazy(() => import('./components/internal/LeafReviewQueue'));
const SitePortal             = lazy(() => import('./components/site/SitePortal'));
const ToolkitBuilder        = lazy(() => import('./components/ToolkitBuilder'));
const InitiativeSetup       = lazy(() => import('./components/InitiativeSetup'));
const InitiativeReadiness   = lazy(() => import('./components/InitiativeReadiness'));
const ProjectConfiguration  = lazy(() => import('./components/ProjectConfiguration'));
const ProjectGovernanceView = lazy(() => import('./components/ProjectGovernanceView'));
const MeasureHub            = lazy(() => import('./components/MeasureHub'));
const SiteProfiles          = lazy(() => import('./components/SiteProfiles'));
const SiteStaff             = lazy(() => import('./components/SiteStaff'));
const DigestAuditLog        = lazy(() => import('./components/DigestAuditLog'));
const AuditLog              = lazy(() => import('./components/AuditLog'));

const SIDEBAR_WIDTH = 252;

/**
 * Initiative quarantine banner + switcher.
 *
 * Hidden when there's 0 or 1 initiative (no scoping ambiguity). When more
 * than one initiative exists, the banner shows the active one and lets the
 * TA user switch — every `adminFetch` after the switch transparently sends
 * `?initiative_id=` so list endpoints return only that initiative's data.
 */
function InitiativeSwitcher() {
    const { initiatives, activeId, active, setActive, pickName } = useActiveInitiative();
    // Hidden only when no initiative exists at all. With 1 initiative we
    // still show a read-only "VIEWING INITIATIVE: <name>" banner so the
    // TA always knows whose data they're looking at; with >1 the dropdown
    // becomes a switcher.
    if (initiatives.length === 0) return null;
    return (
        <Box sx={{
            px: 3, py: 1, display: 'flex', alignItems: 'center', gap: 1.5,
            borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: alpha('#1e88e5', 0.06),
        }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0', letterSpacing: 0.5 }}>
                VIEWING INITIATIVE:
            </Typography>
            {initiatives.length === 1 ? (
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: '#0d47a1' }}
                    data-testid="initiative-banner-single"
                >
                    {pickName(initiatives[0])}
                </Typography>
            ) : (
                <select
                    value={activeId || ''}
                    onChange={(e) => setActive(e.target.value || null)}
                    style={{
                        padding: '4px 8px', borderRadius: 4, border: '1px solid #90caf9',
                        background: 'white', fontSize: 13, fontWeight: 600,
                    }}
                    data-testid="initiative-switcher"
                >
                    <option value="">— select —</option>
                    {initiatives.map((i) => (
                        <option key={i.id} value={i.id}>{pickName(i)}</option>
                    ))}
                </select>
            )}
            {active && initiatives.length > 1 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Sites, audit log, and dashboards are filtered to this initiative.
                </Typography>
            )}
        </Box>
    );
}

/**
 * Compact viewer-context indicator (Task #177).
 *
 * Sits in the internal top bar so staff and site users always know the lens
 * they're operating in: the active role (friendly label) plus the active
 * initiative (or site, for site users). Display-only — the backend remains the
 * real access boundary.
 */
function ViewerContextBadge({
    identity,
    adminUnlocked,
    pitchMode,
}: {
    identity: AuthIdentity | null;
    adminUnlocked: boolean;
    pitchMode: boolean;
}) {
    const { active, pickName } = useActiveInitiative();
    const roleLabel = viewerRoleLabel(identity, { hasAdminCode: adminUnlocked, pitchMode });

    let context: string | null = null;
    if (identity && identity.site_id) {
        context = `Site ${identity.site_id}`;
    } else if (active) {
        context = pickName(active);
    }

    const label = context ? `${roleLabel} · ${context}` : roleLabel;
    return (
        <Tooltip title={`You are viewing as ${roleLabel}${context ? ` — ${context}` : ''}`}>
            <Chip
                icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                label={label}
                size="small"
                data-testid="viewer-context-badge"
                sx={{
                    height: 22,
                    maxWidth: 280,
                    fontSize: '0.63rem',
                    fontWeight: 600,
                    bgcolor: alpha(T.color.teal[700], 0.05),
                    color: T.color.text.muted,
                    border: `1px solid ${alpha(T.color.teal[700], 0.12)}`,
                    '& .MuiChip-icon': { color: 'inherit' },
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
            />
        </Tooltip>
    );
}

/**
 * Safe insufficient-permission view (Task #177).
 *
 * Rendered in place of a panel when the active panel is not in the viewer's
 * allowed set. It NEVER mounts the restricted panel, so none of its
 * contents/data are exposed. A primary action returns the viewer to a panel
 * they can use so they're never stuck.
 */
function UnauthorizedPanel({
    viewerLabel,
    firstAllowedLabel,
    onGoToAllowed,
}: {
    viewerLabel: string;
    firstAllowedLabel: string | null;
    onGoToAllowed: () => void;
}) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <Paper
                data-testid="unauthorized-panel"
                sx={{
                    p: 6,
                    maxWidth: 520,
                    textAlign: 'center',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: T.color.border.light,
                }}
            >
                <LockIcon sx={{ fontSize: 48, color: T.color.coral[500], mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: T.color.text.primary }}>
                    You don't have access to this section
                </Typography>
                <Typography variant="body2" sx={{ color: T.color.text.muted, mb: 1, lineHeight: 1.6 }}>
                    Your current access (<strong>{viewerLabel}</strong>) isn't authorized to view
                    this panel.
                </Typography>
                <Typography variant="body2" sx={{ color: T.color.text.muted, mb: 3, lineHeight: 1.6 }}>
                    If you need it, ask your Synterra Health administrator to grant access.
                </Typography>
                {firstAllowedLabel && (
                    <SynButton variant="primary" onClick={onGoToAllowed}>
                        Go to {firstAllowedLabel}
                    </SynButton>
                )}
            </Paper>
        </Box>
    );
}

/**
 * Initiative-level access notice (Task #313).
 *
 * Rendered in place of a panel when a signed-in staff member is operating under
 * their own role but is NOT assigned to the currently-selected initiative
 * (`InitiativeContext` status === 'permission-denied' with internal access). The
 * backend already returns 404 for every scoped call in that case, so the panels
 * would otherwise look generically empty/blocked. This explains the situation
 * and reuses the existing {@link InitiativeSwitcher} so the user can jump to an
 * initiative they can access without leaving the screen.
 */
function InitiativeAccessDenied({ viewerLabel }: { viewerLabel: string }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <Paper
                data-testid="initiative-access-denied"
                sx={{
                    p: 6,
                    maxWidth: 560,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: T.color.border.light,
                }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <LockIcon sx={{ fontSize: 48, color: T.color.coral[500], mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: T.color.text.primary }}>
                        You don't have access to this initiative
                    </Typography>
                    <Typography variant="body2" sx={{ color: T.color.text.muted, mb: 1, lineHeight: 1.6 }}>
                        Your account (<strong>{viewerLabel}</strong>) isn't assigned to the
                        initiative that's currently selected.
                    </Typography>
                    <Typography variant="body2" sx={{ color: T.color.text.muted, mb: 3, lineHeight: 1.6 }}>
                        Switch to an initiative you can access below, or ask your Synterra Health
                        administrator to add you to this one.
                    </Typography>
                </Box>
                <Box sx={{
                    borderRadius: 2, overflow: 'hidden',
                    border: '1px solid', borderColor: T.color.border.light,
                }}>
                    <InitiativeSwitcher />
                </Box>
            </Paper>
        </Box>
    );
}

interface NavItem { label: string; icon: React.ReactNode; panelIdx: number }
interface NavSection { label: string; items: NavItem[] }

const DEFAULT_SECTIONS: NavSection[] = [
    {
        label: 'Overview',
        items: [
            { label: 'Portfolio',        icon: <DashboardIcon   fontSize="small" />, panelIdx: 0 },
            { label: "Five D's",         icon: <BarChartIcon    fontSize="small" />, panelIdx: 1 },
            { label: 'BIOME',            icon: <TrackChangesIcon fontSize="small" />, panelIdx: 2 },
        ],
    },
    {
        label: 'Intelligence',
        items: [
            { label: 'Simulation Engine', icon: <ScienceIcon      fontSize="small" />, panelIdx: 3 },
            { label: 'CARI Intelligence', icon: <PsychologyIcon   fontSize="small" />, panelIdx: 4 },
            { label: 'TERRA Chat',         icon: <ChatIcon         fontSize="small" />, panelIdx: 5 },
            { label: 'TERRA Chat for Support Providers', icon: <ChatIcon     fontSize="small" />, panelIdx: 6 },
            { label: 'Support Review Queue',   icon: <RateReviewIcon   fontSize="small" />, panelIdx: 7 },
            { label: 'CFIR / ERIC',       icon: <AccountTreeIcon  fontSize="small" />, panelIdx: 8 },
            { label: 'Toolkit Builder',   icon: <MenuBookIcon    fontSize="small" />, panelIdx: 9 },
            { label: 'Peer Exchange',     icon: <ForumIcon       fontSize="small" />, panelIdx: 31 },
        ],
    },
    {
        label: 'Operations',
        items: [
            { label: 'Initiative Setup',    icon: <BusinessIcon    fontSize="small" />, panelIdx: 10 },
            { label: 'Initiative Readiness', icon: <FactCheckIcon  fontSize="small" />, panelIdx: 33 },
            { label: 'Project Config',      icon: <TuneIcon        fontSize="small" />, panelIdx: 11 },
            { label: 'Governance',          icon: <ShieldIcon      fontSize="small" />, panelIdx: 19 },
            { label: 'Record Review',       icon: <RateReviewIcon  fontSize="small" />, panelIdx: 20 },
            { label: 'Source Capture',      icon: <CloudUploadIcon fontSize="small" />, panelIdx: 32 },
            { label: 'Cost Capture',        icon: <ReceiptLongIcon fontSize="small" />, panelIdx: 21 },
            { label: 'Cost Ledger',         icon: <ReceiptLongIcon fontSize="small" />, panelIdx: 28 },
            { label: 'Scenario Library',    icon: <ScienceIcon     fontSize="small" />, panelIdx: 29 },
            { label: 'CEDAR LEAF',          icon: <LibraryBooksIcon fontSize="small" />, panelIdx: 23 },
            { label: 'Research Review',      icon: <FactCheckIcon    fontSize="small" />, panelIdx: 24 },
            { label: 'Suggest a Source',    icon: <LibraryBooksIcon fontSize="small" />, panelIdx: 34 },
            { label: 'Site Profiles',       icon: <PlaceIcon       fontSize="small" />, panelIdx: 12 },
            { label: 'Site Staff',          icon: <PersonAddIcon   fontSize="small" />, panelIdx: 13 },
            { label: 'Verticals',           icon: <FolderIcon      fontSize="small" />, panelIdx: 14 },
            { label: 'Vertical Registry',   icon: <TuneIcon        fontSize="small" />, panelIdx: 25 },
            { label: 'Bulk Upload',         icon: <CloudUploadIcon fontSize="small" />, panelIdx: 15 },
            { label: 'System Health',       icon: <MonitorHeartIcon fontSize="small" />, panelIdx: 16 },
            { label: 'Integration Health',  icon: <MonitorHeartIcon fontSize="small" />, panelIdx: 26 },
            { label: 'AI Usage & Governance', icon: <SmartToyIcon   fontSize="small" />, panelIdx: 27 },
            { label: 'Issue Tracker',       icon: <BugReportIcon   fontSize="small" />, panelIdx: 17 },
            { label: 'Testing Feedback',    icon: <FlagIcon        fontSize="small" />, panelIdx: 30 },
            { label: 'Digest & Audit',     icon: <HistoryIcon     fontSize="small" />, panelIdx: 18 },
            { label: 'Access & Invitations', icon: <HistoryIcon   fontSize="small" />, panelIdx: 35 },
        ],
    },
];

const STORAGE_KEY = 'spartis_nav_order';

function loadNavOrder(): NavSection[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return DEFAULT_SECTIONS;
        const saved: Record<string, number[]> = JSON.parse(raw);
        return DEFAULT_SECTIONS.map(section => {
            const order = saved[section.label];
            if (!order) return section;
            const itemMap = new Map(section.items.map(it => [it.panelIdx, it]));
            const reordered = order
                .filter(idx => itemMap.has(idx))
                .map(idx => itemMap.get(idx)!);
            const remaining = section.items.filter(it => !order.includes(it.panelIdx));
            return { ...section, items: [...reordered, ...remaining] };
        });
    } catch {
        return DEFAULT_SECTIONS;
    }
}

function saveNavOrder(sections: NavSection[]) {
    const order: Record<string, number[]> = {};
    for (const s of sections) {
        order[s.label] = s.items.map(it => it.panelIdx);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
}

// Hidden Validated Measure Hub (Task #134). Intentionally NOT in
// DEFAULT_SECTIONS so it never renders in the default nav, pitch mode, or
// reorder persistence — it is injected at render time only when the
// per-initiative feature flag is on. Its label is registered here so the header
// title resolves correctly when the panel is active via deep link.
const MEASURE_HUB_PANEL_INDEX = 22;
const MEASURE_HUB_LABEL = 'Validated Measure Hub';

const ALL_PANEL_LABELS: Record<number, string> = {};
for (const s of DEFAULT_SECTIONS) {
    for (const it of s.items) {
        ALL_PANEL_LABELS[it.panelIdx] = it.label;
    }
}
ALL_PANEL_LABELS[MEASURE_HUB_PANEL_INDEX] = MEASURE_HUB_LABEL;

// Named indexes for cross-panel navigation, derived from the nav config so
// reordering the `PANELS` array can't silently break click-through.
const CFIR_PANEL_LABEL = 'CFIR / ERIC';
const CFIR_PANEL_INDEX = (() => {
    for (const s of DEFAULT_SECTIONS) {
        for (const it of s.items) {
            if (it.label === CFIR_PANEL_LABEL) return it.panelIdx;
        }
    }
    // Fallback to historical position if the label is renamed; the click
    // handler still degrades gracefully (worst case it activates the wrong
    // panel rather than crashing).
    return 8;
})();

const TA_QUEUE_PANEL_LABEL = 'TERRA Chat for Support Providers';
const TA_QUEUE_PANEL_INDEX = (() => {
    for (const s of DEFAULT_SECTIONS) {
        for (const it of s.items) {
            if (it.label === TA_QUEUE_PANEL_LABEL) return it.panelIdx;
        }
    }
    return 6;
})();

const LEAF_REVIEW_PANEL_LABEL = 'Research Review';
const LEAF_REVIEW_PANEL_INDEX = (() => {
    for (const s of DEFAULT_SECTIONS) {
        for (const it of s.items) {
            if (it.label === LEAF_REVIEW_PANEL_LABEL) return it.panelIdx;
        }
    }
    return 24;
})();

function AppShell() {
    const [active,     setActive]     = useState(0);
    const [sitePortal, setSitePortal] = useState(false);
    const [navSections, setNavSections] = useState<NavSection[]>(loadNavOrder);
    const pitchMode = usePitchMode();
    const measureHubEnabled = useMeasureHubEnabled();

    // ── Role-based access (#162 / A2) ─────────────────────────────────────
    // Single source of truth: the shared InitiativeContext computes the role →
    // allowed-panel matrix (identity from GET /site-users/me + break-glass admin
    // code + pitch/demo mode) exactly once. App no longer recomputes it, so the
    // sidebar filter and the rest of the context can never drift apart. Signed-in
    // staff see their role's panels; break-glass/pitch fall through to the full
    // nav; preview-only visitors get the restricted PUBLIC_PANELS shell. The
    // backend denies the underlying data regardless — this is UX gating only.
    const {
        identity,
        allowedPanelSet,
        status: scopeStatus,
        hasInternalAccess,
        refresh: refreshIdentity,
    } = useInitiativeContext();
    // `identity` still resolving → don't render gated content OR the restricted
    // screen yet (that would flash the wrong panel for a staff member whose role
    // hasn't loaded). The main content shows a neutral spinner while loading.
    const identityResolving = scopeStatus === 'loading';
    // `adminUnlocked` is still needed locally for the display-only viewer badge /
    // role label (the context does not surface the raw break-glass flag).
    const [adminUnlocked, setAdminUnlocked] = useState<boolean>(hasAdminCode);
    React.useEffect(() => {
        const sync = () => setAdminUnlocked(hasAdminCode());
        window.addEventListener('spartis:adminCodeChanged', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('spartis:adminCodeChanged', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);
    // When the active panel isn't permitted for this viewer we no longer silently
    // bounce them to another panel (that was confusing + gave no explanation).
    // Instead the main content renders a safe insufficient-permission view (see
    // UnauthorizedPanel below) and offers a one-click way back to a panel they
    // can use. `firstAllowedPanel` is that fallback target.
    const panelAllowed = allowedPanelSet === null || allowedPanelSet.has(active);
    const firstAllowedPanel = useMemo(() => {
        if (allowedPanelSet === null) return null;
        const sorted = Array.from(allowedPanelSet).sort((a, b) => a - b);
        return sorted.length > 0 ? sorted[0] : null;
    }, [allowedPanelSet]);

    // Pitch mode hides the platform-noise sidebar entry ("Issue Tracker") from
    // the main TA dashboard so demos don't surface internal bug-tracking UI.
    // The underlying panel stays mounted in PANELS so deep links keep working.
    const visibleNavSections = useMemo(() => {
        const roleFiltered = allowedPanelSet === null
            ? navSections
            : navSections
                  .map(section => ({
                      ...section,
                      items: section.items.filter(it => allowedPanelSet.has(it.panelIdx)),
                  }))
                  .filter(section => section.items.length > 0);

        const base = pitchMode
            ? roleFiltered.map(section => ({
                  ...section,
                  items: section.items.filter(it => it.label !== 'Issue Tracker'),
              }))
            : roleFiltered;

        // The hidden Validated Measure Hub is injected into the Operations
        // section only when its per-initiative feature flag is on AND we're not
        // in pitch mode. It is never part of DEFAULT_SECTIONS, so it stays out
        // of nav, demos, and reorder persistence by default.
        if (!measureHubEnabled || pitchMode) return base;
        return base.map(section => {
            if (section.label !== 'Operations') return section;
            if (section.items.some(it => it.panelIdx === MEASURE_HUB_PANEL_INDEX)) {
                return section;
            }
            return {
                ...section,
                items: [
                    ...section.items,
                    {
                        label: MEASURE_HUB_LABEL,
                        icon: <FactCheckIcon fontSize="small" />,
                        panelIdx: MEASURE_HUB_PANEL_INDEX,
                    },
                ],
            };
        });
        // `allowedPanelSet` MUST be a dependency: when a staff member signs in
        // (or out) their identity changes → allowedPanelSet recomputes, and the
        // role-filtered nav has to recompute with it. Omitting it left the nav
        // frozen on the preview/anonymous shell until some other dep changed.
    }, [navSections, pitchMode, measureHubEnabled, allowedPanelSet]);

    const dragItem = useRef<{ sectionIdx: number; itemIdx: number } | null>(null);
    const dragOverItem = useRef<{ sectionIdx: number; itemIdx: number } | null>(null);

    const handleDragStart = useCallback((sectionIdx: number, itemIdx: number) => {
        dragItem.current = { sectionIdx, itemIdx };
    }, []);

    const handleDragEnter = useCallback((sectionIdx: number, itemIdx: number) => {
        dragOverItem.current = { sectionIdx, itemIdx };
    }, []);

    const handleDragEnd = useCallback(() => {
        if (!dragItem.current || !dragOverItem.current) return;
        if (dragItem.current.sectionIdx !== dragOverItem.current.sectionIdx) {
            dragItem.current = null;
            dragOverItem.current = null;
            return;
        }
        const sIdx = dragItem.current.sectionIdx;
        const fromIdx = dragItem.current.itemIdx;
        const toIdx = dragOverItem.current.itemIdx;
        if (fromIdx === toIdx) {
            dragItem.current = null;
            dragOverItem.current = null;
            return;
        }
        setNavSections(prev => {
            const updated = prev.map((section, si) => {
                if (si !== sIdx) return section;
                const items = [...section.items];
                const [moved] = items.splice(fromIdx, 1);
                items.splice(toIdx, 0, moved);
                return { ...section, items };
            });
            saveNavOrder(updated);
            return updated;
        });
        dragItem.current = null;
        dragOverItem.current = null;
    }, []);

    const currentLabel = ALL_PANEL_LABELS[active] ?? 'Portfolio';

    const navigateToCFIRSite = useCallback((siteId?: string) => {
        if (siteId) {
            try { sessionStorage.setItem('cfir.preselectSite', siteId); } catch { /* ignore */ }
        }
        setActive(CFIR_PANEL_INDEX);
    }, []);

    const navigateToTAQueue = useCallback(() => {
        setActive(TA_QUEUE_PANEL_INDEX);
    }, []);

    // Open the Research Review queue pre-filtered to a living-corpus scope
    // (Task #462) — the queue picks the preselect up on mount.
    const navigateToLeafReview = useCallback((scope?: string) => {
        if (scope) {
            try { sessionStorage.setItem('leafReview.preselectScope', scope); } catch { /* ignore */ }
        }
        setActive(LEAF_REVIEW_PANEL_INDEX);
    }, []);

    const PANELS = [
        <InternalDashboard onNavigateToCFIRSite={navigateToCFIRSite} onNavigateToTAQueue={navigateToTAQueue} onNavigateToPanel={setActive} />,
        <FiveDsDashboard />,
        <HELIOSDashboard />,
        <SimulationDashboard />,
        <CARIIntelligence />,
        <AIChat key="synterra-chat" audience="both" contextPanelMode="full" title="TERRA: Implementation Support Engagement and Response Resource Agent" subtitle="All documents — internal + external" />,
        <TAProviderWorkspace key="ta-workspace" />,
        <TAReviewQueue />,
        <CFIRDashboard />,
        <ToolkitBuilder />,
        <InitiativeSetup onNavigate={setActive} />,
        <ProjectConfiguration />,
        <SiteProfiles />,
        <SiteStaff />,
        <Verticals onNavigate={setActive} />,
        <BulkUpload />,
        <HealthView />,
        <PlatformIssueDashboard />,
        <DigestAuditLog />,
        <ProjectGovernanceView />,
        <RecordReviewDashboard />,
        <CostCaptureDashboard />,
        <MeasureHub />,
        <CedarLeafArea onOpenReviewQueue={navigateToLeafReview} />,
        <LeafReviewQueue />,
        <VerticalRegistry />,
        <IntegrationHealthDashboard onNavigate={setActive} />,
        <AIUsageGovernance onNavigate={setActive} />,
        <CostLedger />,
        <ScenarioLibrary />,
        <TestingFeedbackDashboard />,
        <PeerExchange context="admin" />,
        <SourceCapturePanel onNavigateToRecordReview={() => setActive(20)} />,
        <InitiativeReadiness onNavigate={setActive} />,
        <SuggestSource />,
        <AuditLog />,
    ];

    const [safetyAcknowledged, setSafetyAcknowledged] = useState(
        () => sessionStorage.getItem('spartis_safety_acknowledged') === 'true'
    );

    return (
        <>
            {!safetyAcknowledged && (
                <SafetyDisclaimer onAccept={() => setSafetyAcknowledged(true)} />
            )}
            <Box sx={{
                display: 'flex', minHeight: '100vh',
                bgcolor: T.color.surface.canvas,
                backgroundImage: T.gradient.canvas,
            }}>

                {/* ─── Sidebar ─── */}
                {!sitePortal && (
                    <Box sx={{
                        width: SIDEBAR_WIDTH,
                        flexShrink: 0,
                        background: T.gradient.sidebar,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'fixed',
                        top: 0, left: 0,
                        height: '100vh',
                        zIndex: 1200,
                        boxShadow: T.shadow.sidebar,
                    }}>
                        {/* Atmospheric overlay */}
                        <Box sx={{
                            position: 'absolute', inset: 0, pointerEvents: 'none',
                            background: 'radial-gradient(ellipse 130% 60% at 50% 0%, rgba(255,255,255,0.06) 0%, transparent 70%)',
                        }} />

                        {/* Logo */}
                        <Box sx={{ px: 3, pt: 3.5, pb: 2.5, position: 'relative' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: T.gradient.sphere,
                                    boxShadow: T.shadow.sphere,
                                    flexShrink: 0,
                                }} />
                                <Box>
                                    <Typography sx={{
                                        color: '#fff', fontSize: '1.0rem',
                                        letterSpacing: '0.06em', fontWeight: 800, lineHeight: 1.1,
                                    }}>
                                        SYNTERRA
                                    </Typography>
                                    <Typography sx={{
                                        color: T.color.glass.border,
                                        fontSize: '0.58rem', letterSpacing: '0.10em', textTransform: 'uppercase',
                                    }}>
                                        Health
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ borderColor: T.color.glass.border, mx: 2, mb: 1 }} />

                        {/* Navigation */}
                        <Box data-testid="primary-nav" sx={{ flexGrow: 1, overflowY: 'auto', px: 1.5, pb: 1 }}>
                            {visibleNavSections.map((section, sectionIdx) => (
                                <Box key={section.label} mb={0.5}>
                                    <Typography sx={{
                                        color: 'rgba(255,255,255,0.35)',
                                        fontSize: '0.58rem', fontWeight: 700,
                                        letterSpacing: '0.13em', textTransform: 'uppercase',
                                        px: 1.5, py: 0.75,
                                    }}>
                                        {section.label}
                                    </Typography>
                                    <List disablePadding>
                                        {section.items.map((item, itemIdx) => {
                                            const isActive = active === item.panelIdx;
                                            return (
                                                <ListItemButton
                                                    key={item.label}
                                                    draggable
                                                    onDragStart={() => handleDragStart(sectionIdx, itemIdx)}
                                                    onDragEnter={() => handleDragEnter(sectionIdx, itemIdx)}
                                                    onDragEnd={handleDragEnd}
                                                    onDragOver={e => e.preventDefault()}
                                                    onClick={() => setActive(item.panelIdx)}
                                                    sx={{
                                                        borderRadius: `${T.radius.sm}px`,
                                                        mb: 0.25, px: 1.5, py: 0.85,
                                                        position: 'relative',
                                                        bgcolor: isActive ? T.color.glass.bg : 'transparent',
                                                        border: isActive ? `1px solid ${T.color.glass.border}` : '1px solid transparent',
                                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                                        '&:hover .drag-handle': { opacity: 1 },
                                                        '&::before': isActive ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            left: 0, top: '20%', bottom: '20%',
                                                            width: 3, borderRadius: '0 3px 3px 0',
                                                            bgcolor: T.color.coral[500],
                                                            boxShadow: `0 0 8px ${T.color.coral[500]}`,
                                                        } : {},
                                                        transition: 'all 0.14s ease',
                                                        cursor: 'grab',
                                                        '&:active': { cursor: 'grabbing' },
                                                    }}
                                                >
                                                    <DragIndicatorIcon
                                                        className="drag-handle"
                                                        sx={{
                                                            fontSize: 14,
                                                            color: 'rgba(255,255,255,0.25)',
                                                            mr: 0.5,
                                                            opacity: 0,
                                                            transition: 'opacity 0.15s',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <ListItemIcon sx={{ minWidth: 30, color: isActive ? '#fff' : 'rgba(255,255,255,0.48)' }}>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item.label}
                                                        primaryTypographyProps={{
                                                            fontSize: '0.82rem',
                                                            fontWeight: isActive ? 600 : 400,
                                                            color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                                                        }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </Box>
                            ))}
                        </Box>

                        {/* Footer */}
                        <Box sx={{ px: 1.5, pb: 2 }}>
                            {/* Site Portal button */}
                            <Box
                                onClick={() => setSitePortal(true)}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    px: 1.75, py: 1.25, mb: 1,
                                    borderRadius: `${T.radius.sm}px`,
                                    background: `linear-gradient(135deg, ${alpha(T.color.coral[500], 0.18)} 0%, ${alpha(T.color.magenta[500], 0.18)} 100%)`,
                                    border: `1px solid ${alpha(T.color.coral[500], 0.30)}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.16s ease',
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${alpha(T.color.coral[500], 0.26)} 0%, ${alpha(T.color.magenta[500], 0.26)} 100%)`,
                                    },
                                }}
                            >
                                <Box>
                                    <Typography sx={{ color: '#fff', fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.2 }}>
                                        Site Portal
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.60rem' }}>
                                        Site-facing workflow
                                    </Typography>
                                </Box>
                                <OpenInNewIcon sx={{ fontSize: 14, color: alpha(T.color.coral[400], 0.8) }} />
                            </Box>

                            <Box sx={{
                                p: 1.75, borderRadius: `${T.radius.sm}px`,
                                background: T.color.glass.bg,
                                border: `1px solid ${T.color.glass.border}`,
                            }}>
                                <Typography sx={{
                                    color: 'rgba(255,255,255,0.38)', fontSize: '0.56rem',
                                    letterSpacing: '0.10em', textTransform: 'uppercase', mb: 0.6,
                                }}>
                                    Synterra Health
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.60rem', letterSpacing: '0.03em' }}>
                                    CEDAR {'→ '}
                                    <Box component="span" sx={{ color: T.color.magenta[500], fontWeight: 700 }}>TERRA</Box>
                                    {' → '}BIOME{' → '}EPIC
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                                    <Chip label="NIH Phase I" size="small" sx={{
                                        height: 18, fontSize: '0.58rem',
                                        bgcolor: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.50)',
                                        border: `1px solid rgba(255,255,255,0.12)`,
                                    }} />
                                    <Chip label="SBIR" size="small" sx={{
                                        height: 18, fontSize: '0.58rem',
                                        bgcolor: alpha(T.color.magenta[500], 0.18),
                                        color: alpha(T.color.magenta[400], 0.9),
                                        border: `1px solid ${alpha(T.color.magenta[500], 0.30)}`,
                                    }} />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* ─── Main content ─── */}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        ml: sitePortal ? 0 : `${SIDEBAR_WIDTH}px`,
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'margin 0.22s ease',
                    }}
                >
                    {/* Top bar (internal only) */}
                    {!sitePortal && (
                        <Box sx={{
                            px: 4, py: 1.75,
                            bgcolor: alpha(T.color.surface.white, 0.80),
                            backdropFilter: 'blur(16px)',
                            WebkitBackdropFilter: 'blur(16px)',
                            borderBottom: `1px solid ${T.color.border.light}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            position: 'sticky', top: 0, zIndex: 100,
                            boxShadow: '0 1px 0 rgba(10,42,56,0.06)',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{
                                    fontSize: '0.68rem', color: T.color.text.muted,
                                    textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600,
                                }}>
                                    Synterra Health
                                </Typography>
                                <Typography sx={{ color: T.color.border.medium, fontSize: '0.9rem', mx: 0.25 }}>/</Typography>
                                <Typography sx={{ fontSize: '0.85rem', color: T.color.text.primary, fontWeight: 600 }}>
                                    {(identityResolving || panelAllowed) ? currentLabel : 'Access restricted'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ViewerContextBadge
                                    identity={identity}
                                    adminUnlocked={adminUnlocked}
                                    pitchMode={pitchMode}
                                />
                                <StaffAccessControl identity={identity} onChanged={refreshIdentity} />
                                <AdminAccessControl />
                                <Chip
                                    label="Internal · Beta"
                                    size="small"
                                    sx={{
                                        height: 22, fontSize: '0.63rem',
                                        bgcolor: alpha(T.color.teal[700], 0.07),
                                        color: T.color.teal[600],
                                        border: `1px solid ${alpha(T.color.teal[700], 0.12)}`,
                                        fontWeight: 600,
                                    }}
                                />
                            </Box>
                        </Box>
                    )}

                    <InitiativeSwitcher />
                    <StatusBanner />

                    {/* Page content */}
                    <Box sx={{ p: sitePortal ? 0 : 4, flexGrow: 1 }}>
                        <PanelChunkErrorBoundary resetKey={sitePortal ? 'site-portal' : active}>
                        <Suspense
                            fallback={
                                <Box
                                    data-testid="panel-chunk-loading"
                                    sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}
                                >
                                    <CircularProgress sx={{ color: T.color.coral[500] }} />
                                </Box>
                            }
                        >
                        {sitePortal
                            ? <SitePortal onExitPortal={() => setSitePortal(false)} />
                            : identityResolving
                                ? (
                                    <Box
                                        data-testid="panel-loading"
                                        sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}
                                    >
                                        <CircularProgress sx={{ color: T.color.coral[500] }} />
                                    </Box>
                                )
                                : (scopeStatus === 'permission-denied' && hasInternalAccess)
                                    ? <InitiativeAccessDenied
                                          viewerLabel={viewerRoleLabel(identity, { hasAdminCode: adminUnlocked, pitchMode })}
                                      />
                                    : panelAllowed
                                        ? PANELS[active]
                                        : <UnauthorizedPanel
                                              viewerLabel={viewerRoleLabel(identity, { hasAdminCode: adminUnlocked, pitchMode })}
                                              firstAllowedLabel={firstAllowedPanel !== null ? (ALL_PANEL_LABELS[firstAllowedPanel] ?? null) : null}
                                              onGoToAllowed={() => { if (firstAllowedPanel !== null) setActive(firstAllowedPanel); }}
                                          />
                        }
                        </Suspense>
                        </PanelChunkErrorBoundary>
                    </Box>
                </Box>

            </Box>
            {!pitchMode && <BugReportButton pageName={sitePortal ? 'Site Portal' : currentLabel} />}
        </>
    );
}

/**
 * App composes the provider stack and renders the shell inside it. AppShell must
 * be a child of InitiativeProvider so it can consume the shared scope/role
 * context (single source of truth for the role → allowed-panel matrix).
 */
function App() {
    return (
        <AccessGate>
            <InitiativeProvider>
                <PlatformIssueProvider>
                    <ThemeProvider theme={synterraTheme}>
                        <AppShell />
                    </ThemeProvider>
                </PlatformIssueProvider>
            </InitiativeProvider>
        </AccessGate>
    );
}

export default App;
